/**
 * Dialog offering the seven section types before a section is created. The
 * picker never creates anything itself — selecting a card calls `onSelect`
 * and the caller (the sections block) owns creating the seeded draft.
 */
import {
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { TableIconBadge } from '@/shared/ui/data-table';
import { BRIEF_SECTION_TYPES, type BriefSectionType } from '../../lib/sectionTypes';
import { BriefSectionTypeIcon } from '../BriefSectionTypeIcon';

export interface BriefSectionTypePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (type: BriefSectionType) => void;
}

export function BriefSectionTypePicker(props: BriefSectionTypePickerProps) {
  const { t } = useTranslation('knowledge');

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-190 gap-md p-lg" data-testid="brief-section-type-picker">
        <DialogHeader>
          <DialogTitle>{t('knowledgeHub.briefSectionTypePicker.addBriefSection')}</DialogTitle>
          <DialogDescription>
            {t('knowledgeHub.briefSectionTypePicker.selectTheTypeOfContent')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-xs">
          {BRIEF_SECTION_TYPES.map((entry) => (
            <button
              key={entry.type}
              type="button"
              data-testid={`brief-section-type-card-${entry.type}`}
              onClick={() => props.onSelect(entry.type)}
              className="rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {/* `outline` is the variant that carries a border; the default one is
                  shadow-only. The prototype draws these flat, so the shadow that both
                  variants ship comes back off. */}
              <Card
                variant="outline"
                className="h-full gap-xs rounded-lg p-md shadow-none transition-colors hover:border-border-strong hover:bg-accent"
              >
                {/* Sized down from the badge's 40-square default: the prototype draws this
                    tile at 36. The component is reused for its `bg-surface`/dark pairing. */}
                <TableIconBadge className="size-9 rounded-md">
                  <BriefSectionTypeIcon type={entry.type} className="size-5 text-foreground" />
                </TableIconBadge>
                <span className="flex flex-col gap-2xs">
                  <span className="text-sm font-medium text-foreground">{t(entry.labelKey)}</span>
                  <span className="text-sm text-muted-foreground">{t(entry.hintKey)}</span>
                </span>
              </Card>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
